import React, { useContext, useState, useEffect, useMemo, memo } from "react";
import InfiniteScroll from "react-infinite-scroller";
import CourseCard from "../cards/CourseCard";
import { fetchRecommendedCourses } from "../../../services/coursesApi";
import { toast } from "react-toastify";
import { useInfiniteQuery } from "react-query";
import { Language } from "../../../App";
import NewCourseCardShimmer from "../shimmers/NewCourseCardShimmer";

function getGridColumnCount() {
  if (typeof window === "undefined") return 2;
  const width = window.innerWidth;
  if (width >= 2200) return 3;
  if (width > 991) return 2;
  return 1;
}

function useGridColumns() {
  const [columns, setColumns] = useState(getGridColumnCount);

  useEffect(() => {
    const handleResize = () => {
      setColumns(getGridColumnCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return columns;
}

function RecommendedCourses({ recommendData = [] }) {
  const { lang } = useContext(Language);
  const columns = useGridColumns();
  const initialShimmerCount = columns === 1 ? 4 : columns * 2;
  const loaderShimmerCount = columns === 1 ? 2 : columns * 2;

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["recommendedCourses"],
    queryFn: ({ pageParam = 2 }) =>
      fetchRecommendedCourses({ page: pageParam, limit: 12 }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.current_page || lastPage?.data?.current_page;
      const lastPageNum = lastPage?.last_page || lastPage?.data?.last_page;
      if (currentPage && lastPageNum && currentPage < lastPageNum) {
        return currentPage + 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const subsequentCourses = useMemo(
    () => data?.pages?.flatMap((page) => page?.data?.data || page?.data || []) || [],
    [data?.pages]
  );

  const allCourses = useMemo(() => {
    const initialList = Array.isArray(recommendData) ? recommendData : [];
    if (!subsequentCourses.length) return initialList;

    const seen = new Set(initialList.map((c) => c?.id).filter(Boolean));
    const uniqueSubsequent = subsequentCourses.filter((c) => c?.id && !seen.has(c.id));
    return [...initialList, ...uniqueSubsequent];
  }, [recommendData, subsequentCourses]);

  const hasMore = Boolean(
    hasNextPage !== undefined
      ? hasNextPage
      : (recommendData && recommendData.length >= 6)
  );

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasMore) {
      fetchNextPage();
    }
  };

  const showInitialShimmer = isLoading && (!allCourses || allCourses.length === 0);

  return (
    <>
      <div className="cr-section-header">
        <div className="cr-section-title-wrap">
          <h3 className="cr-section-title">
            {lang.recomended_for_you || "Recommended for You"}
          </h3>
          <p className="cr-section-subtitle">
            Personalized clinical masterclasses matching your specialty and interests
          </p>
        </div>
      </div>

      {showInitialShimmer ? (
        <div className="cr-courses-grid">
          {Array.from({ length: initialShimmerCount }).map((_, index) => (
            <NewCourseCardShimmer width="100%" key={`rec-init-shimmer-${index}`} />
          ))}
        </div>
      ) : allCourses.length > 0 ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={handleLoadMore}
          hasMore={hasMore}
          useWindow={true}
          threshold={350}
          loader={
            <div className="cr-courses-grid mt-3" key="recommended-infinite-loader">
              {Array.from({ length: loaderShimmerCount }).map((_, index) => (
                <NewCourseCardShimmer width="100%" key={`rec-load-shimmer-${index}`} />
              ))}
            </div>
          }
        >
          <div className="cr-courses-grid">
            {allCourses.map((course, index) => (
              <CourseCard
                key={`recommend-${course.id || index}`}
                course={course}
                width="100%"
              />
            ))}
          </div>
        </InfiniteScroll>
      ) : null}
    </>
  );
}

export default memo(RecommendedCourses);
